import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { ContractLineBillingCycleOutput, ContractStatusEnum, VariableContractLineOutput }
  from '@/graphql/contracts/_generated_/contracts';
import { clearVariableLinesSelection, useSelectedVariableLines }
  from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import { clearLinesSelection, useSelectedLines }
  from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import OptionButton from '@/components/common/OptionButton/OptionButton';
import AssignPurchaseOrderModal from './components/AssignPurchaseOrderModal/AssignPurchaseOrderModal';
import EditIcon from '@/components/common/OptionButton/icons/Edit/EditIcon';
import { notificationService } from '@/services/notification/notification.client';
import { useBulkAssignPurchaseOrder } from '@/pages/ContractDetails/ContractLines/hooks/useBulkAssignPurchaseOrder';
import { Fields } from './components/AssignPurchaseOrderModal/validation';
import { rightsidePanel_isOpen } from '../../RightPanel.state';

export default function AssignPurchaseOrder() {
  const { contractId }: { contractId: UUID } = useParams();
  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const variableSelected = Boolean(selectedVariableLineRows?.length);

  const [isOpenAssignPurchaseOrderModal, setIsOpenAssignPurchaseOrderModal] = useState(false);
  const { bulkAssignPurchaseOrder } = useBulkAssignPurchaseOrder(true, contractId, variableSelected);
  const [invoiceToCustomerId, setInvoiceToCustomerId] = useState<string>('');
  const [contractLineIds, setContractLineIds] = useState<string[]>([]);
  const [totalLinesCount, setTotalLinesCount] = useState<number>(0);
  const [totalLinesWithPoCount, setTotalLinesWithPoCount] = useState<number>(0);
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);

  const handleSubmit = (values: Fields) => {
    bulkAssignPurchaseOrder(values.purchaseOrderId, contractLineIds, () => {
      clearLinesSelection();
      clearVariableLinesSelection();
      setIsOpenRightPanel(false);
    });
    setIsOpenAssignPurchaseOrderModal(false);
  };

  const handleOnClick = () => {
    const invoiceToCustomersIds = [
      ...selectedLineRows.map((slr) => {
        const contractLine = slr.payload.contractLine as ContractLineBillingCycleOutput;
        return contractLine.invoiceToCustomerId;
      }),
      ...selectedVariableLineRows.map((svlr) => {
        const contractLine = svlr.payload.contractLine as VariableContractLineOutput;
        return contractLine.invoiceToCustomerId;
      })
    ];

    const firstInvoiceToCustomerId = invoiceToCustomersIds[0];
    setInvoiceToCustomerId(firstInvoiceToCustomerId);
    setTotalLinesCount(invoiceToCustomersIds.length);
    const allSameInvoiceToCustomer = invoiceToCustomersIds.every((itc) => itc === firstInvoiceToCustomerId
      && itc !== null && itc !== undefined);

    if (allSameInvoiceToCustomer) {
      setIsOpenAssignPurchaseOrderModal(true);
      const allContractLineIds = [
        ...selectedLineRows.map((slr) => {
          const contractLine = slr.payload.contractLine as ContractLineBillingCycleOutput;
          return contractLine.id;
        }),
        ...selectedVariableLineRows.map((svlr) => {
          const contractLine = svlr.payload.contractLine as VariableContractLineOutput;
          return contractLine.iD;
        })
      ];
      setContractLineIds(allContractLineIds);

      const allContractLinePurchaseOrders = [
        ...selectedLineRows
          .map((slr) => {
            const contractLine = slr.payload.contractLine as ContractLineBillingCycleOutput;
            return contractLine.poNumber;
          })
          .filter((poNumber) => poNumber !== null && poNumber !== undefined),
        ...selectedVariableLineRows
          .map((svlr) => {
            const contractLine = svlr.payload.contractLine as VariableContractLineOutput;
            return contractLine.poNumber;
          })
          .filter((poNumber) => poNumber !== null && poNumber !== undefined)
      ];
      setTotalLinesWithPoCount(allContractLinePurchaseOrders.length);
    }
    else {
      setIsOpenAssignPurchaseOrderModal(false);
      notificationService.open({
        title: 'Error',
        text: 'All selected lines must have the same Invoice To Account',
        type: 'danger'
      });
    }
  };

  const getContractLineStatus =
    (contractLine: ContractLineBillingCycleOutput | VariableContractLineOutput) => {
      return ((contractLine as ContractLineBillingCycleOutput)?.status ||
        (contractLine as VariableContractLineOutput)?.status) as
        | ContractStatusEnum
        | undefined;
    };

  const editableStatuses = [
    ContractStatusEnum.Active,
    ContractStatusEnum.ActiveDNR,
  ];

  if (
    !selectedLineRows.filter((slr) =>
      editableStatuses.includes(
        getContractLineStatus(slr.payload.contractLine) ??
        ContractStatusEnum.Cancelled
      )
    ).length &&
    !selectedVariableLineRows.filter((svlr) =>
      editableStatuses.includes(
        getContractLineStatus(svlr.payload.contractLine) ??
        ContractStatusEnum.Cancelled
      )
    ).length
  ) {
    return null;
  }

  return (
    <>
      <OptionButton onClick={handleOnClick}
        Icon={< EditIcon />}>
        Assign Purchase Order
      </OptionButton>

      {isOpenAssignPurchaseOrderModal && (
        <AssignPurchaseOrderModal
          onClose={() => setIsOpenAssignPurchaseOrderModal(false)}
          invoiceToCustomerId={invoiceToCustomerId}
          totalLinesCount={totalLinesCount}
          totalLinesWithPoCount={totalLinesWithPoCount}
          onSubmit={(handleSubmit)}
          contractId={contractId}
        />
      )}
    </>
  );
}
